import classnames from "classnames/bind";
import { TFunction, useTranslation } from "next-i18next";
import { useRouter } from "next/router";
import qs from "querystring";
import React, { useEffect, useMemo, useState } from "react";
import InfiniteScroll from "react-infinite-scroll-component";

import {
    DEFAULT_OFFSET_VALUE,
    DEFAULT_PER_PAGE_VALUE,
    MAX_NUM_FEATURED_STORES,
} from "@/shared/constants";
import { StoreDirectorySortBy } from "@/shared/store-directory.constants";

import { UI_CONTEXTS } from "@/analytics/constants";

import { useCurrentLocale } from "@/context/LanguageContext";

import { ControlHeader } from "@/components/control-header/control-header";
import { DropdownMenuItem } from "@/components/dropdown-menus/dropdown-menu-items/dropdown-menu-item-interface";
import Loader from "@/components/loaders/loader";
import { StoreTiles } from "@/components/store-tiles/store-tiles";

import { useCategoryStores } from "@/hooks/useCategory";

import { CategoryProps } from "@/interfaces/category-interface";
import { StoreProps } from "@/interfaces/stores-interface";

import StoreDirectoryDescription from "./store-directory-description";
import StoreDirectoryNoResults from "./store-directory-no-results";
import styles from "./storeDirectory.module.scss";

const cx = classnames.bind(styles);

const getSortOptions = (t: TFunction) => {
    const filterRelevanceOptionText = t("store.filterRelevanceOptionText"),
        filterNewestOptionText = t("store.filterNewestOptionText"),
        filterMostPopularOptionText = t("store.filterMostPopularOptionText");
    const sortByOptions: Array<DropdownMenuItem> = [
        {
            id: 1,
            name: filterRelevanceOptionText,
            value: StoreDirectorySortBy.Relevance,
        },
        {
            id: 2,
            name: filterNewestOptionText,
            value: StoreDirectorySortBy.Newest,
        },
        {
            id: 3,
            name: filterMostPopularOptionText,
            value: StoreDirectorySortBy.MostPopular,
        },
    ];

    return sortByOptions;
};

interface Props {
    currentCategory: CategoryProps;
    parentCategory: CategoryProps;
}
const StoreDirectory: React.FC<Props> = ({ currentCategory, parentCategory }) => {
    const router = useRouter();
    const { sort_by, has_in_store, has_offer } = router.query;

    const { t } = useTranslation();
    const locale = useCurrentLocale();

    const sortByOptions = useMemo(() => getSortOptions(t), [t]);
    const defaultSort = sortByOptions[0].value;

    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [storesList, setStoresList] = useState<Array<StoreProps>>([]);
    const [featuredStoresList, setFeaturedStoresList] = useState<Array<StoreProps>>([]);
    const [offset, setOffset] = useState(parseInt(router.query.offset as string, 10) || 0);
    const [hasMore, setHasMore] = useState(false);
    const [hasOffers, setHasOffers] = useState(has_offer === "true");
    const [hasInStore, setHasInStore] = useState(has_in_store === "true");
    const [sortByValue, setSortByValue] = useState<string>(
        sort_by && typeof sort_by === "string" ? sort_by : defaultSort
    );

    const loadMoreStores = () => {
        setOffset(offset + DEFAULT_OFFSET_VALUE);
    };
    const [queryString, setQueryString] = useState(
        qs.stringify({
            has_in_store: hasInStore,
            has_offer: hasOffers,
            offset: offset,
            per_page: DEFAULT_PER_PAGE_VALUE,
            sort_by: sortByValue,
        })
    );
    const [featuredQueryString, setFeaturedQueryString] = useState(
        qs.stringify({
            has_in_store: hasInStore,
            has_offer: hasOffers,
            offset: 0,
            featured: true,
            per_page: MAX_NUM_FEATURED_STORES,
            sort_by: sortByValue,
        })
    );

    const stores = useCategoryStores(currentCategory.slug, { queryString, initialState: null });
    const featuredStores = useCategoryStores(currentCategory.slug, {
        queryString: featuredQueryString,
    });

    useEffect(() => {
        setOffset(0);
        setStoresList([]);
        setFeaturedStoresList([]);
    }, [hasInStore, hasOffers, sortByValue, locale, router.asPath]);

    useEffect(() => {
        setHasOffers(router.query.has_offer === "true");
        setHasInStore(router.query.has_in_store === "true");
        setSortByValue(
            router.query.sort_by && typeof router.query.sort_by === "string"
                ? router.query.sort_by
                : defaultSort
        );
    }, [router.query.has_offer, router.query.has_in_store, router.query.sort_by, defaultSort]);

    useEffect(() => {
        if (!Array.isArray(stores)) {
            setHasMore(true);
            return;
        }

        setHasMore(stores.length > 0);
        setIsLoading(stores.length === 0);
        setStoresList([...storesList, ...stores]);
    }, [stores]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!Array.isArray(featuredStores)) {
            return;
        }
        setFeaturedStoresList(featuredStores);
    }, [featuredStores]);

    useEffect(() => {
        setQueryString(
            qs.stringify({
                has_in_store: hasInStore,
                has_offer: hasOffers,
                offset: offset,
                per_page: DEFAULT_PER_PAGE_VALUE,
                sort_by: sortByValue,
            })
        );
        setFeaturedQueryString(
            qs.stringify({
                has_in_store: hasInStore,
                has_offer: hasOffers,
                offset: 0,
                featured: true,
                per_page: MAX_NUM_FEATURED_STORES,
                sort_by: sortByValue,
            })
        );
    }, [hasInStore, hasOffers, offset, sortByValue, locale]);

    const pushState = (params) => {
        const queryParams = { ...router.query, ...params };
        if (!queryParams.has_in_store) delete queryParams.has_in_store;
        if (!queryParams.has_offer) delete queryParams.has_offer;
        if (queryParams.sort_by === defaultSort) delete queryParams.sort_by;
        router.push({ pathname: router.pathname, query: queryParams }, undefined, {
            shallow: true,
        });
    };

    const clearFilters = async () => {
        setStoresList([]);
        setFeaturedStoresList([]);
        pushState({ has_offer: false, has_in_store: false });
    };

    const storesDirectoryClassName = cx({
        [styles.storesDirectoryContainer]: true,
    });

    const ResultsElement =
        !isLoading && storesList.length === 0 ? (
            <StoreDirectoryNoResults onClick={clearFilters} />
        ) : (
            <div className={storesDirectoryClassName}>
                <div className={styles.featuredTilesContainer}>
                    <StoreTiles
                        category={currentCategory}
                        parentCategory={parentCategory}
                        stores={featuredStoresList}
                        analytics={{
                            uiContext: UI_CONTEXTS.CATEGORY_TILE,
                        }}
                        featured
                    />
                </div>
                <InfiniteScroll
                    dataLength={storesList.length}
                    hasMore={hasMore}
                    loader={<Loader />}
                    next={loadMoreStores}
                    scrollThreshold={0.8}
                >
                    <StoreTiles
                        category={currentCategory}
                        parentCategory={parentCategory}
                        stores={storesList}
                        analytics={{
                            uiContext: UI_CONTEXTS.CATEGORY_TILE,
                        }}
                    />
                </InfiniteScroll>
            </div>
        );

    return (
        <div data-testid="store-directory">
            <ControlHeader
                locale={locale}
                currentCategory={currentCategory}
                parentCategory={parentCategory}
                hasOffers={hasOffers}
                hasInStore={hasInStore}
                handleOffersClick={() => pushState({ has_offer: !hasOffers })}
                handleInStoreClick={() => pushState({ has_in_store: !hasInStore })}
                handleSortByClick={(val) => pushState({ sort_by: val })}
                sortByOptions={sortByOptions}
                inStoreLocationUrl={`/categories/${currentCategory.slug}/locations`}
                uiContext={UI_CONTEXTS.CATEGORY_TILE}
            />
            {ResultsElement}
            <StoreDirectoryDescription category={currentCategory} />
        </div>
    );
};

export default StoreDirectory;
